// src/contexts/StockScreenerContext.js
import React, { createContext, useEffect } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { METRIC_FIELDS } from '../constants/metricFields';

export const StockScreenerContext = createContext();

// Default metrics to display (P/E, Gross margin, Net margin)
const DEFAULT_METRIC_KEYS = [
  'priceToEarningsRatio',
  'grossProfitMargin',
  'netProfitMargin'
];

const DEFAULT_METRICS = METRIC_FIELDS
  .filter(m => DEFAULT_METRIC_KEYS.includes(m.value))
  .map(m => ({ label: m.label, key: m.value }));

export function StockScreenerProvider({ children }) {
  // Stocks in the screener table
  const [screenedStocks, setScreenedStocks] = useLocalStorage(
    'screenerStocks',
    [] // will be auto-filled from /list-stocks-search
  );

  // Comparison selection
  const [selectedForComparison, setSelectedForComparison] = useLocalStorage(
    'screenerSelectedForComparison',
    []
  );

  // Which metrics columns are shown in the table
  const [selectedMetrics, setSelectedMetrics] = useLocalStorage(
    'screenerSelectedMetrics',
    DEFAULT_METRICS
  );

  // Never hydrate legacy provider payloads. User choices remain persistent.
  useEffect(() => {
    try { window.localStorage.removeItem('screenerMetricsData'); } catch {}
  }, []);

  // Active slider filters in the sidebar
  const [activeFilters, setActiveFilters] = useLocalStorage(
    'screenerActiveFilters',
    []
  );

  // --- Stock management ---

  const addStock = (symbol) => {
    setScreenedStocks(stocks =>
      stocks.includes(symbol) ? stocks : [...stocks, symbol]
    );
  };

  const removeStock = (symbol) => {
    setScreenedStocks(stocks => stocks.filter(s => s !== symbol));
    setSelectedForComparison(selected => selected.filter(s => s !== symbol));
  };

  const clearAllStocks = () => {
    setScreenedStocks([]);
    setSelectedForComparison([]);
  };

  const isStockScreened = (symbol) => screenedStocks.includes(symbol);

  // --- Comparison selection ---

  const toggleSelection = (symbol) => {
    setSelectedForComparison(selected =>
      selected.includes(symbol)
        ? selected.filter(s => s !== symbol)
        : [...selected, symbol]
    );
  };

  const clearSelection = () => {
    setSelectedForComparison([]);
  };

  // --- Filter management ---

  const clearFilters = () => {
    setActiveFilters([]);
  };

  const contextValue = {
    // State
    screenedStocks,
    selectedForComparison,
    selectedMetrics,
    activeFilters,

    // Stock management
    addStock,
    removeStock,
    clearAllStocks,
    isStockScreened,

    // Comparison selection
    toggleSelection,
    clearSelection,

    // Metrics management
    setSelectedMetrics,

    // Filter management
    setActiveFilters,
    clearFilters,

    // Direct setters if needed
    setScreenedStocks,
    setSelectedForComparison,
  };

  return (
    <StockScreenerContext.Provider value={contextValue}>
      {children}
    </StockScreenerContext.Provider>
  );
}
